import "./character-sheet-metrics.scss";
import React, { useState } from "react";
import { CharacterHelpers, LocalCharacterInitializer } from "../../../../../entities/characters/Character";
import { ArmorIcon, EditIcon, MoneyIcon, ResilienceIcon } from "../../../../icons";
import { MovementIcon } from "../../../../icons/CharacterIcons";
import { ExternalClickDetector } from "../../../../../utils/events/ExternalClickDetector";
import { ThemedButton } from "../../../../inputs/buttons/ThemedButton";
import { RestrictNumber } from "../../../../../entities/Utilities";
import { useAppDispatch, useAppSelector } from "../../../../../store/configureStore";
import { CharacterPropertyActions } from "../../../../../store/stores/collection/characters/actions/CharacterPropertyActions";
import { Die } from "../../../../figures/Die";
import { OnDCPassIcon } from "../../../../icons/IndicatorIcons";

type Props = {
    character: LocalCharacterInitializer;
    viewOnMobile: boolean;
}

export const CharacterSheetMetrics: React.FC<Props> = (props: Props) => {
    const { character, viewOnMobile } = props;
    const allItems = useAppSelector(app => app.library.items);
    const dispatch = useAppDispatch();
    const [editingMoney, setEditingMoney] = useState(false);
    const [moneyDelta, setMoneyDelta] = useState(0);
    const [moneyOverride, setMoneyOverride] = useState(character.money);

    const onAddMoney = () => {
        const newMoney = character.money + moneyDelta;
        updateMoney(newMoney);
    }

    const onRemoveMoney = () => {
        const newMoney = character.money - moneyDelta;
        updateMoney(newMoney);
    }

    const onChangeMoney = (e: React.ChangeEvent<HTMLInputElement>) => {
        const moneyDelta = parseInt(e.target.value);
        if (!isNaN(moneyDelta)){
            setMoneyDelta(moneyDelta);
        }
    }

    const updateMoney = (newMoney: number) => {
        newMoney = RestrictNumber(newMoney, 0, Number.MAX_SAFE_INTEGER);
        dispatch(CharacterPropertyActions.updateMoney(character, newMoney));
        setMoneyDelta(0);
        setMoneyOverride(newMoney);
        setEditingMoney(false);
    }

    const moneyFormatter = Intl.NumberFormat("en", { notation: 'compact' });

    const defensiveItem = CharacterHelpers.getDefensiveItem(character, allItems);
    return (
        <div className={`character-sheet-metrics sheet-section ${viewOnMobile ? "viewing" : "unviewing"}`}>
            <div className="metrics-container">
                <div className="metrics-row">
                    <div className="character-sheet-metric">
                        <div className="icon-value">
                            <ArmorIcon />
                            <span className="defense-value">{defensiveItem?.armor ?? 0}</span>
                            <Die>{defensiveItem?.armorDc ?? 0}</Die>
                            <OnDCPassIcon />
                            {defensiveItem?.armorSuccess ?? 0}
                        </div>
                        <label>Armor</label>
                    </div>
                    <div className="character-sheet-metric">
                        <div className="icon-value">
                            <MovementIcon />{CharacterHelpers.getMovement(character)}
                        </div>
                        <label>Speed</label>
                    </div>
                </div>
                <div className="metrics-row">
                    
                    <div className="character-sheet-metric">
                        <div className="icon-value">
                            <ResilienceIcon />
                            <span className="defense-value">{defensiveItem?.resilience ?? 0}</span>
                            <Die>{defensiveItem?.resilienceDc ?? 0}</Die>
                            <OnDCPassIcon />
                            {defensiveItem?.resilienceSuccess ?? 0}
                        </div>
                        <label>Resilience</label>
                    </div>
                    <div className="character-sheet-metric interactable-button" title={`${character.money.toLocaleString()} chips`} onClick={() => setEditingMoney(true)} role="button">
                        <div className="icon-value">
                            <MoneyIcon />{moneyFormatter.format(character.money)}
                        </div>
                        <label>Chips&nbsp;<EditIcon /></label>
                    </div>
                </div>
                {editingMoney && (
                    <ExternalClickDetector className="metric-editor option-window" onExternalClickDetected={() => setEditingMoney(false)}>
                        <div className="title">
                            Update Chips
                        </div>
                        <div className="editor">
                            <ThemedButton className="increase-button" onClick={onAddMoney}>
                                Add
                            </ThemedButton>
                            <input type="number" value={moneyDelta} onChange={onChangeMoney} min={0} />
                            <ThemedButton className="decrease-button" onClick={onRemoveMoney}>
                                Remove
                            </ThemedButton>
                            <hr />
                            <input type="number" value={moneyOverride} min={0} onChange={e => setMoneyOverride(parseInt(e.target.value))} />
                            <ThemedButton onClick={() => updateMoney(moneyOverride)}>
                                Set Chips
                            </ThemedButton>
                        </div>
                    </ExternalClickDetector>
                )}
            </div>
        </div>
    );
}